import { getOutletById } from 'app/connection/Connection';
import { useConnection } from 'app/connection/hooks/useConnection';
import { PremiseOnSinglePremise } from 'app/premises/Premise';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { G } from '@mobily/ts-belt';
import { Alert, Button, CircularProgress, Grid } from '@mui/material';
import { useDeleteFiber } from '../../../hooks/useDeleteFiber';
import { Fiber, Outlet } from '../../../Outlet';
import * as styles from './styles';

export const DeleteFiber: FC<{
  premise: PremiseOnSinglePremise;
  outlet: Outlet;
  fiber: Fiber;
  onSuccess?: Lambda<void, void>;
  onCancel?: Lambda<void, void>;
}> = ({ premise, outlet, fiber, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { data, isLoading: isLoadingConnection } = useConnection(premise.propertyId);

  const {
    mutate,
    isLoading: isLoadingMutateConnection,
    isError: isErrorMutateConnection,
    isSuccess: isSuccessMutateConnection,
  } = useDeleteFiber(premise.propertyId, outlet.id);

  useEffect(() => {
    if (isErrorMutateConnection) {
      toast.error(t('error.generalMessage'));
    }
  }, [isErrorMutateConnection]);

  useEffect(() => {
    if (isSuccessMutateConnection) {
      toast.success(t('general.updatedSuccessfully'));

      if (G.isNotNullable(onSuccess)) {
        onSuccess();
      }
    }
  }, [isSuccessMutateConnection]);

  const deleteFiber = useCallback(() => {
    if (G.isNotNullable(data)) {
      const pickedOutlet = getOutletById(data.serialized2, outlet.id);

      if (G.isNotNullable(pickedOutlet)) {
        mutate({ fiberId: fiber.id });
      }
    }
  }, [data, fiber, outlet]);

  const hasContract = useMemo(() => {
    return !!fiber.contract;
  }, [fiber.contract]);

  const isLoading = useMemo(() => {
    return isLoadingConnection || isLoadingMutateConnection;
  }, [isLoadingConnection, isLoadingMutateConnection]);

  return (
    <Grid container>
      {isLoading && (
        <Grid container sx={styles.loaderContainer}>
          <CircularProgress />
        </Grid>
      )}
      {!isLoading && (
        <Grid container>
          {hasContract && <Alert severity="error">{t('fiber.delete.contractAlert')}</Alert>}
          {!hasContract && (
            <Grid container sx={styles.deleteFibersButtonsContainer}>
              <Button
                variant="contained"
                onClick={() => G.isNotNullable(onCancel) && onCancel()}
                data-testid="delete-fiber-cancel"
              >
                {t('general.cancel')}
              </Button>
              <Button variant="outlined" onClick={deleteFiber} data-testid="delete-fiber-accept">
                {t('general.accept')}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};
